/**
 * Users Tab for Admin Client Detail Page
 * List of client users with organization memberships, search, and org management.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, XClose } from "@untitledui/icons";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { Toggle } from "@/components/base/toggle/toggle";
import { useAdminApi } from "@/services/api/admin-api";
import type { OrganizationAdminListItemDto, UserAdminListItemDto } from "@/types/api/admin";

// =============================================================================
// TYPES
// =============================================================================

interface UsersTabProps {
    clientId: number;
}

const PAGE_SIZE = 25;

// =============================================================================
// ORG MANAGEMENT PANEL (inline per-user)
// =============================================================================

interface OrgManagementPanelProps {
    clientId: number;
    user: UserAdminListItemDto;
    allOrgs: OrganizationAdminListItemDto[];
    onUpdated: () => void;
}

function OrgManagementPanel({ clientId, user, allOrgs, onUpdated }: OrgManagementPanelProps) {
    const adminApi = useAdminApi();
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [targetSystemUserId, setTargetSystemUserId] = useState(user.targetSystemUserId ?? "");
    const [isSavingUserId, setIsSavingUserId] = useState(false);
    const [userIdSaved, setUserIdSaved] = useState(false);
    const [restrictToOwn, setRestrictToOwn] = useState(user.restrictToOwnDocuments);
    const [isSavingRestriction, setIsSavingRestriction] = useState(false);

    const handleToggleRestriction = useCallback(
        async (next: boolean) => {
            // Optimistic update; revert on failure.
            setRestrictToOwn(next);
            setIsSavingRestriction(true);
            setError(null);
            try {
                await adminApi.updateUser(clientId, user.id, { restrictToOwnDocuments: next });
                onUpdated();
            } catch (err) {
                setRestrictToOwn(!next);
                setError(err instanceof Error ? err.message : "Failed to update Private workspace setting.");
            } finally {
                setIsSavingRestriction(false);
            }
        },
        [adminApi, clientId, user.id, onUpdated],
    );

    const handleSaveTargetSystemUserId = useCallback(async () => {
        setIsSavingUserId(true);
        setError(null);
        setUserIdSaved(false);
        try {
            await adminApi.updateUser(clientId, user.id, {
                targetSystemUserId: targetSystemUserId.trim() || null,
            });
            setUserIdSaved(true);
            onUpdated();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update target system user ID.");
        } finally {
            setIsSavingUserId(false);
        }
    }, [adminApi, clientId, user.id, targetSystemUserId, onUpdated]);

    const assignedOrgIds = useMemo(() => new Set(user.organizations.map((o) => o.organizationId)), [user.organizations]);

    const availableOrgs: SelectItemType[] = useMemo(
        () => allOrgs.filter((o) => o.isActive && !assignedOrgIds.has(o.id)).map((o) => ({ id: String(o.id), label: `${o.name} (${o.code})` })),
        [allOrgs, assignedOrgIds],
    );

    const handleAdd = useCallback(
        async (key: string | number | null) => {
            if (!key) return;
            setIsSaving(true);
            setError(null);
            try {
                await adminApi.addUserOrganization(clientId, user.id, Number(key));
                onUpdated();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to add organization.");
            } finally {
                setIsSaving(false);
            }
        },
        [adminApi, clientId, user.id, onUpdated],
    );

    const handleRemove = useCallback(
        async (orgId: number) => {
            setIsSaving(true);
            setError(null);
            try {
                await adminApi.removeUserOrganization(clientId, user.id, orgId);
                onUpdated();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to remove organization.");
            } finally {
                setIsSaving(false);
            }
        },
        [adminApi, clientId, user.id, onUpdated],
    );

    return (
        <div className="border-brand-secondary bg-primary_subtle space-y-3 rounded-lg border px-4 py-3">
            <div className="text-xs font-medium text-secondary">
                Manage <span className="font-semibold text-primary">{user.email}</span>
            </div>

            {/* Private workspace restriction */}
            <div className="space-y-1">
                <Toggle
                    size="sm"
                    label="Private workspace"
                    hint="User sees only documents they personally uploaded."
                    isSelected={restrictToOwn}
                    isDisabled={isSavingRestriction}
                    onChange={handleToggleRestriction}
                />
            </div>

            {/* Target System User ID */}
            <div className="space-y-1">
                <label className="text-xs font-medium text-secondary">Target System User ID</label>
                <div className="flex items-center gap-2">
                    <div className="max-w-xs flex-1">
                        <Input
                            placeholder="e.g. ERP user ID..."
                            value={targetSystemUserId}
                            onChange={setTargetSystemUserId}
                            size="sm"
                            isDisabled={isSavingUserId}
                        />
                    </div>
                    <Button
                        color="secondary"
                        size="sm"
                        onClick={handleSaveTargetSystemUserId}
                        isLoading={isSavingUserId}
                        isDisabled={targetSystemUserId === (user.targetSystemUserId ?? "")}
                    >
                        Save
                    </Button>
                    {userIdSaved && <span className="text-xs text-success-primary">Saved</span>}
                </div>
            </div>

            {/* Current memberships */}
            {user.organizations.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                    {user.organizations.map((org) => (
                        <span
                            key={org.organizationId}
                            className="inline-flex items-center gap-1 rounded-full border border-secondary bg-primary px-2.5 py-1 text-xs font-medium text-primary"
                        >
                            {org.organizationName || org.organizationCode}
                            <button
                                type="button"
                                className="cursor-pointer rounded-full p-0.5 text-quaternary transition-colors hover:bg-error-secondary hover:text-error-primary"
                                onClick={() => handleRemove(org.organizationId)}
                                disabled={isSaving}
                                title={`Remove ${org.organizationCode}`}
                            >
                                <XClose className="size-3" />
                            </button>
                        </span>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-quaternary">No organizations assigned.</p>
            )}

            {/* Add dropdown */}
            {availableOrgs.length > 0 && (
                <div className="max-w-xs">
                    <Select
                        label="Add to organization"
                        items={availableOrgs}
                        selectedKey={null}
                        onSelectionChange={handleAdd}
                        placeholder="Select organization..."
                        size="sm"
                        isDisabled={isSaving}
                    >
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>
            )}

            {availableOrgs.length === 0 && user.organizations.length > 0 && (
                <p className="text-xs text-quaternary">User belongs to all available organizations.</p>
            )}

            {error && <p className="text-xs text-error-primary">{error}</p>}
            {isSaving && <LoadingIndicator size="sm" />}
        </div>
    );
}

// =============================================================================
// USERS TAB
// =============================================================================

export function UsersTab({ clientId }: UsersTabProps) {
    const adminApi = useAdminApi();
    const [users, setUsers] = useState<UserAdminListItemDto[]>([]);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const debounceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
    const [allOrgs, setAllOrgs] = useState<OrganizationAdminListItemDto[]>([]);
    const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
    const [isPulling, setIsPulling] = useState(false);
    const [pullResult, setPullResult] = useState<{ imported: number; skipped: number } | null>(null);
    const [pullError, setPullError] = useState<string | null>(null);
    // Global "Private workspace" toggle (applies to all users of the client).
    const [pendingGlobalValue, setPendingGlobalValue] = useState<boolean | null>(null);
    const [isApplyingGlobal, setIsApplyingGlobal] = useState(false);
    const [globalError, setGlobalError] = useState<string | null>(null);

    // Fetch all organizations for this client (once)
    useEffect(() => {
        let cancelled = false;
        async function fetchOrgs() {
            try {
                const orgs = await adminApi.listClientOrganizations(clientId);
                if (!cancelled) setAllOrgs(orgs);
            } catch {
                // non-blocking — org management just won't work
            }
        }
        fetchOrgs();
        return () => {
            cancelled = true;
        };
    }, [adminApi, clientId]);

    // Debounce search input
    useEffect(() => {
        debounceTimer.current = setTimeout(() => {
            setDebouncedSearch(search);
            setSkip(0);
        }, 300);
        return () => clearTimeout(debounceTimer.current);
    }, [search]);

    const fetchUsers = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await adminApi.listClientUsers(clientId, {
                search: debouncedSearch || undefined,
                pageSize: PAGE_SIZE,
                skip,
            });
            setUsers(res.data);
            setTotal(res.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load users.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId, debouncedSearch, skip]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handlePullFromClerk = useCallback(async () => {
        setIsPulling(true);
        setPullResult(null);
        setPullError(null);
        try {
            const result = await adminApi.pullUsersFromClerk(clientId);
            setPullResult(result);
            await fetchUsers();
        } catch (err) {
            setPullError(err instanceof Error ? err.message : "Failed to pull users from Clerk.");
        } finally {
            setIsPulling(false);
        }
    }, [adminApi, clientId, fetchUsers]);

    // The global switch reflects the loaded/visible users; the action itself is always client-wide.
    const allRestricted = useMemo(() => users.length > 0 && users.every((u) => u.restrictToOwnDocuments), [users]);

    const handleConfirmGlobal = useCallback(async () => {
        if (pendingGlobalValue === null) return;
        setIsApplyingGlobal(true);
        setGlobalError(null);
        try {
            await adminApi.bulkUpdateUserRestriction(clientId, pendingGlobalValue);
            await fetchUsers();
            setPendingGlobalValue(null);
        } catch (err) {
            setGlobalError(err instanceof Error ? err.message : "Failed to update Private workspace for all users.");
        } finally {
            setIsApplyingGlobal(false);
        }
    }, [adminApi, clientId, pendingGlobalValue, fetchUsers]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

    return (
        <div className="space-y-6">
            <section className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="text-lg font-semibold text-primary">Users</h3>
                    <p className="mt-1 text-sm text-tertiary">Users belonging to this client and their organization memberships.</p>
                </div>
                <Toggle
                    size="sm"
                    label="Private workspace (all users)"
                    hint="Apply private workspace to every user in this client."
                    isSelected={allRestricted}
                    isDisabled={isApplyingGlobal || isLoading || users.length === 0}
                    onChange={(next) => setPendingGlobalValue(next)}
                />
            </section>

            {/* Search + Pull from Clerk */}
            <div className="flex items-center gap-3">
                <div className="max-w-sm flex-1">
                    <Input placeholder="Search by email, name..." value={search} onChange={setSearch} size="sm" />
                </div>
                <Button color="secondary" size="sm" onClick={handlePullFromClerk} isLoading={isPulling}>
                    Pull from Clerk
                </Button>
            </div>

            {/* Pull result feedback */}
            {pullResult && (
                <p className="text-sm text-success-primary">
                    Imported {pullResult.imported} user{pullResult.imported !== 1 ? "s" : ""}, {pullResult.skipped} already existed.
                </p>
            )}
            {pullError && <p className="text-sm text-error-primary">{pullError}</p>}

            {/* Table */}
            {isLoading ? (
                <div className="py-8">
                    <LoadingIndicator size="sm" label="Loading users..." />
                </div>
            ) : error ? (
                <p className="text-sm text-error-primary">{error}</p>
            ) : users.length === 0 ? (
                <p className="py-8 text-center text-sm text-quaternary">No users found.</p>
            ) : (
                <>
                    <div className="overflow-x-auto rounded-lg border border-secondary">
                        <table className="w-full text-left text-sm">
                            <thead className="border-b border-secondary bg-secondary_subtle">
                                <tr>
                                    <th className="px-4 py-3 font-medium text-secondary">Email</th>
                                    <th className="px-4 py-3 font-medium text-secondary">First Name</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Last Name</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Role</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Status</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Private workspace</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Target User ID</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Organizations</th>
                                    <th className="w-24 px-4 py-3 font-medium text-secondary">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-secondary">
                                {users.map((user) => {
                                    const isExpanded = expandedUserId === user.id;
                                    const needsOrg = user.organizations.length === 0;
                                    const rowBg = needsOrg ? "bg-error-subtle hover:bg-error-secondary" : "hover:bg-primary_hover";
                                    return (
                                        <>
                                            <tr key={user.id} className={`group ${rowBg}`}>
                                                <td className="px-4 py-3 font-medium text-primary">{user.email}</td>
                                                <td className="px-4 py-3 text-tertiary">{user.firstName}</td>
                                                <td className="px-4 py-3 text-tertiary">{user.lastName ?? "—"}</td>
                                                <td className="px-4 py-3">
                                                    <Badge color="brand" size="sm" type="pill-color">
                                                        {user.role}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Badge color={user.isActive ? "success" : "gray"} size="sm" type="pill-color">
                                                        {user.isActive ? "Active" : "Inactive"}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Badge color={user.restrictToOwnDocuments ? "warning" : "gray"} size="sm" type="pill-color">
                                                        {user.restrictToOwnDocuments ? "On" : "Off"}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3 text-tertiary">
                                                    {user.targetSystemUserId || <span className="text-quaternary">—</span>}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {user.organizations.length === 0 ? (
                                                        <span className="text-quaternary">None</span>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-1">
                                                            {user.organizations.map((org) => (
                                                                <Badge key={org.organizationId} color="gray" size="sm" type="pill-color">
                                                                    {org.organizationCode}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Button
                                                        color={isExpanded ? "primary" : "secondary"}
                                                        size="sm"
                                                        onClick={() => setExpandedUserId(isExpanded ? null : user.id)}
                                                    >
                                                        {isExpanded ? "Close" : "Manage"}
                                                    </Button>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr key={`${user.id}-panel`} className={needsOrg ? "bg-error-subtle" : ""}>
                                                    <td colSpan={9} className="px-4 pt-0 pb-4">
                                                        <OrgManagementPanel clientId={clientId} user={user} allOrgs={allOrgs} onUpdated={fetchUsers} />
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {total > PAGE_SIZE && (
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-tertiary">
                                Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total}
                            </p>
                            <div className="flex gap-2">
                                <Button color="secondary" size="sm" isDisabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}>
                                    Previous
                                </Button>
                                <Button color="secondary" size="sm" isDisabled={currentPage >= totalPages} onClick={() => setSkip(skip + PAGE_SIZE)}>
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Global Private workspace confirmation dialog */}
            <ModalOverlay
                isOpen={pendingGlobalValue !== null}
                onOpenChange={(open) => !open && !isApplyingGlobal && setPendingGlobalValue(null)}
                isDismissable={!isApplyingGlobal}
            >
                <Modal className="max-w-md">
                    <Dialog>
                        <div className="flex w-full flex-col gap-5 rounded-xl border border-secondary bg-primary p-6 shadow-xl">
                            <div className="flex items-start gap-3">
                                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-warning-secondary">
                                    <AlertTriangle className="size-5 text-warning-primary" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-primary">
                                        Turn Private workspace {pendingGlobalValue ? "On" : "Off"} for all users
                                    </h2>
                                    <p className="mt-1 text-sm text-tertiary">
                                        This will turn Private workspace <span className="font-semibold text-primary">{pendingGlobalValue ? "On" : "Off"}</span>{" "}
                                        for <span className="font-semibold text-primary">all users</span> of this client, overriding each user's individual
                                        setting. Continue?
                                    </p>
                                </div>
                            </div>

                            {globalError && <p className="text-sm text-error-primary">{globalError}</p>}

                            <div className="flex justify-end gap-3">
                                <Button color="secondary" size="md" onClick={() => setPendingGlobalValue(null)} isDisabled={isApplyingGlobal}>
                                    Cancel
                                </Button>
                                <Button color="primary" size="md" onClick={handleConfirmGlobal} isDisabled={isApplyingGlobal}>
                                    {isApplyingGlobal ? "Applying..." : "Apply to all"}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </div>
    );
}
